/**
 * Error handling utilities and custom error classes for the BD SDK.
 *
 * @remarks
 * This module provides comprehensive error handling including custom error classes,
 * error transformation utilities, and integration with external SDK errors.
 *
 * @module Error Handling
 * @category Internal
 * @packageDocumentation
 */
// External SDK error classes used by consolidated DeFi SDK
import { FetchError, RequiredError, ResponseError } from "@blockdaemon/defi-api-sdk";
import pkg from "../../../package.json";
const BD_SDK_VERSION = pkg.version || "unknown";
const STAKEDECODING_SDK_VERSION = pkg.dependencies?.["@blockdaemon/transaction-verifier"] || "unknown";
const DEFI_SDK_NAME = "@blockdaemon/defi-api-sdk";
const DEFI_SDK_PACKAGE_NAME = "@blockdaemon/defi-api-sdk";
const DEFI_SDK_VERSION = ((pkg.dependencies &&
    pkg.dependencies[DEFI_SDK_PACKAGE_NAME]) ||
    "unknown");
const STAKEWISE_SDK_NAME = "@stakewise/v3-sdk";
const STAKEWISE_SDK_VERSION = ((pkg.dependencies &&
    pkg.dependencies[STAKEWISE_SDK_NAME]) ||
    "unknown");
/** Self-serve sign-up and API key management (use when requests are unauthenticated). */
// snyk:ignore:javascript/HardcodedNonCryptoSecret
const BD_SDK_RESOLUTION_URL_AUTH = "https://app.blockdaemon.com/";
/** Plan tiers, rate limits, and SDK-related offerings (use when requests are rate limited). */
const BD_SDK_RESOLUTION_URL_RATE_LIMIT = "https://www.blockdaemon.com/get-started";
function isEAxiosErrorLike(error) {
    return (error instanceof Error &&
        "isAxiosError" in error &&
        error.isAxiosError === true);
}
/**
 * Sets {@link IBlockdaemonSDKError.resolutionUrl} for 401 (unauthenticated) and 429 (rate limit).
 * Call from the top of each error pipeline (after normalization and {@link attachReproducibility}).
 */
function finalizeSdkError(base) {
    if (base.status === 401) {
        base.resolutionUrl = BD_SDK_RESOLUTION_URL_AUTH;
    }
    else if (base.status === 429) {
        base.resolutionUrl = BD_SDK_RESOLUTION_URL_RATE_LIMIT;
    }
    return base;
}
/**
 * Returns a concise, human-readable error detail based on HTTP status code.
 */
function getHumanReadableDetail(status) {
    switch (status) {
        case 400:
            return "Bad request";
        case 401:
            return `Unauthorized: missing or invalid credentials. Get a free API key at ${BD_SDK_RESOLUTION_URL_AUTH}`;
        case 403:
            return "Forbidden: insufficient permissions";
        case 404:
            return "Not found";
        case 405:
            return "Method not allowed";
        case 408:
            return "Request timeout";
        case 409:
            return "Conflict";
        case 422:
            return "Unprocessable entity";
        case 429:
            return `Too many requests: rate limited. See plan tiers and get started at ${BD_SDK_RESOLUTION_URL_RATE_LIMIT}`;
        case 500:
            return "Internal server error";
        case 502:
            return "Bad gateway";
        case 503:
            return "Service unavailable";
        case 504:
            return "Gateway timeout";
        default:
            return status ? `Request failed (${status})` : "Request failed";
    }
}
/**
 * Core error normalization for SDK calls.
 *
 * @internal
 * @remarks
 * This helper converts errors thrown by underlying SDK dependencies into our
 * standardized {@link IBlockdaemonSDKError} shape. It inspects well-known error
 * classes from the consolidated DeFi SDK and maps them as follows:
 *
 * - {@link ResponseError}: Maps to `type: "api_error"`, includes `status` and
 *   `instance` (response URL) when available.
 * - {@link FetchError}: Maps to `type: "network_error"` for transport-level failures.
 * - {@link RequiredError}: Maps to `type: "validation_error"` with `status: 400`.
 * - Any other error: Maps to `type: "unknown_error"` preserving the message/stack.
 *
 * In all cases, `dependencyName` and `dependencyVersion` are populated from the
 * provided options so callers can identify the originating library/version.
 *
 * This is not part of the public API surface; consumers should use
 * {@link handleDefiSdkError} or {@link handleStakewiseSdkError} instead.
 *
 * @param error - The thrown error from a dependency call
 * @param opts - Metadata describing the originating dependency
 * @returns A normalized {@link IBlockdaemonSDKError}
 */
function constructUserErrorResponseCore(error, opts) {
    const stack = error instanceof Error ? error.stack : undefined;
    if (error instanceof ResponseError) {
        return {
            type: "api_error",
            status: error.response.status,
            detail: getHumanReadableDetail(error.response.status),
            errorDescription: error.message,
            instance: error.response.url,
            dependencyName: opts.dependencyName,
            dependencyVersion: opts.dependencyVersion,
            bdSdkVersion: BD_SDK_VERSION || "unknown",
            stakeDecodingSdkVersion: STAKEDECODING_SDK_VERSION || "unknown",
            stack,
            timestamp: new Date().toISOString(),
        };
    }
    if (isEAxiosErrorLike(error)) {
        const status = error.response?.status;
        const url = error.config?.url || error.response?.config?.url;
        return {
            type: status ? "api_error" : "network_error",
            status,
            detail: status ? getHumanReadableDetail(status) : "Network error",
            errorDescription: error.message,
            instance: url,
            dependencyName: opts.dependencyName,
            dependencyVersion: opts.dependencyVersion,
            bdSdkVersion: BD_SDK_VERSION || "unknown",
            stakeDecodingSdkVersion: STAKEDECODING_SDK_VERSION || "unknown",
            stack,
            timestamp: new Date().toISOString(),
        };
    }
    if (error instanceof FetchError) {
        return {
            type: "network_error",
            detail: "Network error",
            dependencyName: opts.dependencyName,
            dependencyVersion: opts.dependencyVersion,
            bdSdkVersion: BD_SDK_VERSION || "unknown",
            stakeDecodingSdkVersion: STAKEDECODING_SDK_VERSION || "unknown",
            stack,
            timestamp: new Date().toISOString(),
        };
    }
    if (error instanceof RequiredError) {
        return {
            type: "validation_error",
            status: 400,
            detail: getHumanReadableDetail(400),
            errorDescription: error.message,
            dependencyName: opts.dependencyName,
            dependencyVersion: opts.dependencyVersion,
            bdSdkVersion: BD_SDK_VERSION || "unknown",
            stakeDecodingSdkVersion: STAKEDECODING_SDK_VERSION || "unknown",
            stack,
            timestamp: new Date().toISOString(),
        };
    }
    return {
        type: "unknown_error",
        detail: error instanceof Error && error.message ? error.message : "Unknown error",
        errorDescription: error instanceof Error ? error.message : String(error),
        dependencyName: opts.dependencyName,
        dependencyVersion: opts.dependencyVersion,
        bdSdkVersion: BD_SDK_VERSION || "unknown",
        stakeDecodingSdkVersion: STAKEDECODING_SDK_VERSION || "unknown",
        stack,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Wrapper for consolidated DeFi SDK errors.
 *
 * @remarks
 * @internal
 * Public entry-point for error normalization when calling the consolidated
 * DeFi SDK. Delegates to the internal {@link handleSdkError} with the
 * appropriate dependency metadata pre-filled.
 */
function constructUserErrorResponse(error) {
    return constructUserErrorResponseCore(error, {
        dependencyName: DEFI_SDK_NAME,
        dependencyVersion: DEFI_SDK_VERSION,
    });
}
/**
 * Reads and enriches HTTP errors from the consolidated DeFi SDK with server-provided details.
 *
 * @remarks
 * - Designed for wrapping DeFi SDK calls so callers receive a normalized {@link IBlockdaemonSDKError}.
 * - For HTTP errors (ResponseError), attempts to read the response body.
 *   - If body is JSON (RFC7807-style), extracts `detail | message | title` into `errorDescription`.
 *   - If body is plain text, stores it in `errorDescription`.
 * - `detail` is set to a concise, human-readable summary derived from the HTTP status
 *   (e.g., 400 → "Bad request: wrong parameters").
 * - Falls back to {@link handleDefiSdkError} when the body cannot be read/parsed.
 * - Emits debug logs when a `logger` is provided.
 *
 * @param error - Raw error thrown by the underlying SDK
 * @param logger - Optional logger used to emit debug diagnostics
 * @returns A normalized {@link IBlockdaemonSDKError} with concise `detail` and verbose `errorDescription`
 *
 * @example
 * ```ts
 * try {
 *   await tokensApi.getAssets({ tokenAddress: "0x..." });
 * } catch (err) {
 *   return await handleDefiSdkErrorAsync(err, logger);
 * }
 * ```
 */
export async function handleDefiSdkError(error, logger, repro) {
    if (error instanceof ResponseError && error.response) {
        throw await handleResponseErrorWithBody(error, logger, repro);
    }
    if (isEAxiosErrorLike(error)) {
        throw handleAxiosErrorWithBody(error, logger, repro);
    }
    throw buildFallbackError(error, repro);
}
function createEnrichedReproducibility(repro, responseUrl) {
    return {
        method: repro?.method,
        url: repro?.url || responseUrl,
        headers: repro?.headers,
        body: repro?.body,
    };
}
function applyParsedBodyToError(base, text, logger) {
    if (!text)
        return;
    let parsed;
    try {
        parsed = JSON.parse(text);
        logger.silly("handleDefiSdkErrorAsync: parsed JSON problem details");
    }
    catch {
        logger.silly("handleDefiSdkErrorAsync: non-JSON body; using raw text");
    }
    if (parsed && typeof parsed === "object") {
        const obj = parsed;
        const firstDetail = obj.detail ||
            obj.message ||
            obj.title;
        base.detail = getHumanReadableDetail(base.status);
        base.errorDescription = firstDetail || text;
        if (obj.trace && typeof obj.trace === "object") {
            base.trace = obj.trace;
        }
    }
    else {
        base.detail = getHumanReadableDetail(base.status);
        base.errorDescription = text;
    }
}
function attachReproducibility(base, reproducibility) {
    base.requestForReproducibility = reproducibility;
    base.commandForReproducibility = "TBD";
}
// Intentional two-layer pattern: this function is the *builder* — it constructs
// and returns the IBlockdaemonSDKError so the caller (handleDefiSdkError) can
// throw it.
async function handleResponseErrorWithBody(error, logger, repro) {
    try {
        logger.silly("handleDefiSdkErrorAsync: ResponseError detected", {
            status: error.response.status,
            url: error.response.url,
        });
        const text = await error.response.text();
        logger.silly("handleDefiSdkErrorAsync: response body read", {
            length: text?.length || 0,
        });
        const base = constructUserErrorResponse(error);
        const enrichedRepro = createEnrichedReproducibility(repro, error.response.url);
        applyParsedBodyToError(base, text, logger);
        attachReproducibility(base, enrichedRepro);
        return finalizeSdkError(base);
    }
    catch (e) {
        logger.silly("handleDefiSdkErrorAsync: failed reading/parsing response body; falling back", {
            error: e instanceof Error ? e.message : String(e),
        });
        return buildFallbackError(error, repro);
    }
}
function handleAxiosErrorWithBody(error, logger, repro) {
    const status = error.response?.status;
    const url = error.config?.url || error.response?.config?.url || "";
    logger.silly("handleDefiSdkError: AxiosError detected", { status, url });
    const base = constructUserErrorResponse(error);
    const enrichedRepro = createEnrichedReproducibility(repro, url);
    const responseData = error.response?.data;
    if (responseData && typeof responseData === "string") {
        applyParsedBodyToError(base, responseData, logger);
    }
    else if (responseData && typeof responseData === "object") {
        applyParsedBodyToError(base, JSON.stringify(responseData), logger);
    }
    attachReproducibility(base, enrichedRepro);
    return finalizeSdkError(base);
}
function buildFallbackError(error, repro) {
    const fallback = constructUserErrorResponse(error);
    if (repro?.url) {
        attachReproducibility(fallback, repro);
    }
    return finalizeSdkError(fallback);
}
/**
 * Wrapper for Stakewise SDK errors.
 *
 * @remarks
 * @internal
 * Public entry-point for error normalization when calling the Stakewise SDK.
 * Delegates to the internal {@link handleSdkError} with Stakewise metadata.
 */
export function handleStakewiseSdkError(error) {
    return finalizeSdkError(constructUserErrorResponseCore(error, {
        dependencyName: STAKEWISE_SDK_NAME,
        dependencyVersion: STAKEWISE_SDK_VERSION,
    }));
}
/**
 * Generic SDK error handler with default "bd-sdk" dependency info
 * Can be used as a fallback when specific SDK error handlers are not available
 */
export function handleGenericSdkError(error) {
    return finalizeSdkError(constructUserErrorResponseCore(error, {
        dependencyName: "bd-sdk",
        dependencyVersion: BD_SDK_VERSION || "unknown",
    }));
}
/**
 * Type guard function to determine if a result is a IBlockdaemonSDKError.
 *
 * @remarks
 * This utility function provides type-safe error detection for API responses.
 * It checks for the presence of required error fields and validates their types
 * to ensure the object conforms to the IBlockdaemonSDKError interface.
 *
 * @param result - The result to check for error status
 * @returns True if the result is a IBlockdaemonSDKError, false otherwise
 *
 * @example
 * ```typescript
 * const result = await api.getBalances({ address: "0x123..." });
 *
 * if (isBlockdaemonSDKError(result)) {
 *   console.error(`Error: ${result.title}`);
 * } else {
 *   console.log(`Native balance: ${result.data.native}`);
 * }
 * ```
 *
 * @public
 */
export function isBlockdaemonSDKError(result) {
    return (result !== null &&
        typeof result === "object" &&
        "type" in result &&
        typeof result.type === "string" &&
        ("status" in result ? typeof result.status === "number" : true));
}
/**
 * Formats an IBlockdaemonSDKError into a human-readable string for logging.
 *
 * @remarks
 * This utility is especially useful in CI environments where errors logged as
 * `[object Object]` provide no diagnostic value. It extracts and formats all
 * relevant error fields in a structured, readable format.
 *
 * @param error - The SDK error to format
 * @returns A formatted string with all relevant error details
 *
 * @example
 * ```typescript
 * if (isBlockdaemonSDKError(result)) {
 *   throw new Error(`API call failed: ${formatSDKError(result)}`);
 * }
 * ```
 *
 * @public
 */
export function formatSDKError(error) {
    const parts = [];
    // Core error info
    parts.push(`[${error.type}]`);
    if (error.status !== undefined) {
        parts.push(`Status: ${error.status}`);
    }
    if (error.detail) {
        parts.push(`Detail: ${error.detail}`);
    }
    if (error.errorDescription && error.errorDescription !== error.detail) {
        parts.push(`Description: ${error.errorDescription}`);
    }
    if (error.instance) {
        parts.push(`Instance: ${error.instance}`);
    }
    if (error.resolutionUrl) {
        parts.push(`Resolve: ${error.resolutionUrl}`);
    }
    // Dependency info (useful for debugging SDK integrations)
    if (error.dependencyName) {
        parts.push(`Dependency: ${error.dependencyName}@${error.dependencyVersion || "unknown"}`);
    }
    // Version info
    if (error.bdSdkVersion) {
        parts.push(`BD-SDK: v${error.bdSdkVersion}`);
    }
    // Reproducibility info (curl command or request details)
    if (error.commandForReproducibility && error.commandForReproducibility !== "TBD") {
        parts.push(`Reproduce: ${error.commandForReproducibility}`);
    }
    else if (error.requestForReproducibility?.url) {
        const req = error.requestForReproducibility;
        parts.push(`Request: ${req.method || "GET"} ${req.url}`);
    }
    // Trace info (if available)
    if (error.trace && Object.keys(error.trace).length > 0) {
        parts.push(`Trace: ${JSON.stringify(error.trace)}`);
    }
    return parts.join(" | ");
}
/**
 * Error thrown when a chain namespace cannot be detected or is not supported.
 *
 * @remarks
 * This error is thrown when attempting to parse a chain ID that does not have
 * a recognizable namespace (e.g., "eip155", "solana").
 *
 * @example
 * ```typescript
 * try {
 *   const vault = getVaultAddress("invalid-chain");
 * } catch (error) {
 *   if (isChainInvalidNamespaceError(error)) {
 *     console.error(`Invalid namespace for chain: ${error.chainId}`);
 *   }
 * }
 * ```
 */
export class ChainInvalidNamespaceError extends Error {
    chainId;
    type;
    detail;
    bdSdkVersion;
    constructor(chainId) {
        const detail = `Cannot detect namespace for chain ID: "${chainId}". Expected CAIP-2 format (namespace:reference).`;
        super(detail);
        this.name = "ChainInvalidNamespaceError";
        this.chainId = chainId;
        this.type = "chain_invalid_namespace";
        this.detail = detail;
        this.bdSdkVersion = BD_SDK_VERSION || "unknown";
    }
}
/**
 * Type guard to check if an error is a ChainInvalidNamespaceError.
 *
 * @param error - The error to check
 * @returns True if the error is a ChainInvalidNamespaceError
 */
export function isChainInvalidNamespaceError(error) {
    return error instanceof ChainInvalidNamespaceError;
}
/**
 * Error thrown when a chain ID is invalid for the given namespace.
 *
 * @remarks
 * This error is thrown when:
 * - For EVM (eip155): the chain ID is not a valid numeric value
 * - For Solana: the chain ID is not a valid alias (mainnet/testnet) or genesis hash
 *
 * @example
 * ```typescript
 * try {
 *   const vault = getVaultAddress("eip155:invalid");
 * } catch (error) {
 *   if (isChainInvalidChainIdError(error)) {
 *     console.error(`Invalid chain ID: ${error.chainId} for namespace: ${error.namespace}`);
 *   }
 * }
 * ```
 */
export class ChainInvalidChainIdError extends Error {
    chainId;
    namespace;
    type;
    detail;
    errorDescription;
    bdSdkVersion;
    constructor(chainId, namespace, reason) {
        const baseMessage = `Invalid chain ID "${chainId}" for namespace "${namespace}"`;
        const detail = reason ? `${baseMessage}: ${reason}` : baseMessage;
        super(detail);
        this.name = "ChainInvalidChainIdError";
        this.chainId = chainId;
        this.namespace = namespace;
        this.type = "chain_invalid_chain_id";
        this.detail = detail;
        this.errorDescription = reason;
        this.bdSdkVersion = BD_SDK_VERSION || "unknown";
    }
}
/**
 * Type guard to check if an error is a ChainInvalidChainIdError.
 *
 * @param error - The error to check
 * @returns True if the error is a ChainInvalidChainIdError
 */
export function isChainInvalidChainIdError(error) {
    return error instanceof ChainInvalidChainIdError;
}
/**
 * Error thrown when a validator vote pubkey is not in the configured whitelist.
 *
 * @remarks
 * This error is thrown during Solana staking operations when the requested
 * validator is not in the customer's configured whitelist (`ISolanaConfig`).
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.staking.buildStakeTx({
 *     vaultAddress: "someValidator...",
 *     // ...
 *   });
 * } catch (error) {
 *   if (isValidatorNotWhitelistedError(error)) {
 *     console.error(`Validator ${error.validatorAddress} not allowed on ${error.network}`);
 *   }
 * }
 * ```
 */
export class ValidatorNotWhitelistedError extends Error {
    /** The validator vote pubkey that was not whitelisted */
    validatorAddress;
    /** The network where the validation failed (e.g., "solana:mainnet") */
    network;
    constructor(validatorAddress, network) {
        super(`Validator vote pubkey is not allowed by solana config: ${validatorAddress} on ${network}`);
        this.name = "ValidatorNotWhitelistedError";
        this.validatorAddress = validatorAddress;
        this.network = network;
    }
}
/**
 * Type guard to check if an error is a ValidatorNotWhitelistedError.
 *
 * @param error - The error to check
 * @returns True if the error is a ValidatorNotWhitelistedError
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.staking.buildStakeTx(request);
 * } catch (error) {
 *   if (isValidatorNotWhitelistedError(error)) {
 *     console.error("Validator not in whitelist:", error.validatorAddress);
 *   }
 * }
 * ```
 */
export function isValidatorNotWhitelistedError(error) {
    return error instanceof ValidatorNotWhitelistedError;
}
/**
 * Error thrown when a Solana RPC call fails.
 *
 * @remarks
 * This error wraps JSON-RPC errors returned by Solana RPC endpoints.
 * It provides structured access to the error message and additional
 * context to help diagnose and handle RPC failures. It is simple on purpose, until we use a BD SDK to handle the errors coming from the RPC.
 *
 * @example
 * ```typescript
 * try {
 *   const result = await chainService.callSolanaRpc("solana:mainnet", "getVoteAccounts", []);
 * } catch (error) {
 *   if (isSolanaRPCError(error)) {
 *     console.error(`Solana RPC error: ${error.rpcMessage}`);
 *   }
 * }
 * ```
 */
export class SolanaRPCError extends Error {
    /** Error type identifier for RFC 7807 compliance */
    type = "solana_rpc_error";
    /** The raw error message from the Solana RPC response */
    rpcMessage;
    /** HTTP status code if the error originated from HTTP layer */
    status;
    /** The RPC method that was called */
    method;
    /** The chain ID where the error occurred */
    chainId;
    /** Additional data from the RPC error response */
    data;
    /** Version of the BD SDK */
    bdSdkVersion;
    /**
     * Creates a new SolanaRPCError.
     *
     * @param code - The JSON-RPC error code
     * @param rpcMessage - The error message from the RPC response
     * @param options - Additional context for the error
     */
    constructor(rpcMessage, options) {
        super(`Solana RPC error: ${rpcMessage}`);
        this.name = "SolanaRPCError";
        this.rpcMessage = rpcMessage;
        this.method = options?.method;
        this.chainId = options?.chainId;
        this.data = options?.data;
        this.status = options?.status;
        this.bdSdkVersion = BD_SDK_VERSION || "unknown";
        // Capture stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, SolanaRPCError);
        }
    }
    /**
     * Creates a SolanaRPCError from an HTTP response error (non-200 status).
     *
     * @param status - HTTP status code
     * @param statusText - HTTP status text
     * @param options - Additional context
     * @returns A new SolanaRPCError instance
     */
    static fromHttpError(status, statusText, options) {
        return new SolanaRPCError(`HTTP ${status}: ${statusText}`, {
            ...options,
            status,
        });
    }
    /**
     * Creates a SolanaRPCError from a JSON-RPC error response.
     *
     * @param jsonRpcError - The error object from the JSON-RPC response
     * @param options - Additional context
     * @returns A new SolanaRPCError instance
     */
    static fromRpcResponse(jsonRpcError, options) {
        return new SolanaRPCError(jsonRpcError.message, {
            ...options,
            data: jsonRpcError.data,
        });
    }
    /**
     * Converts the error to a structured IBlockdaemonSDKError format.
     */
    toBlockdaemonError() {
        const detail = this.status === 401 || this.status === 429
            ? getHumanReadableDetail(this.status)
            : this.message;
        return finalizeSdkError({
            type: this.type,
            status: this.status,
            detail,
            errorDescription: this.rpcMessage,
            trace: {
                rpcMessage: this.rpcMessage,
                method: this.method,
                chainId: this.chainId,
                data: this.data,
            },
            bdSdkVersion: this.bdSdkVersion,
            stack: this.stack,
        });
    }
}
/**
 * Type guard to check if an error is a SolanaRPCError.
 *
 * @param error - The error to check
 * @returns True if the error is a SolanaRPCError
 *
 * @example
 * ```typescript
 * try {
 *   await chainService.getSolanaVoteAccounts("solana:mainnet", validatorAddress);
 * } catch (error) {
 *   if (isSolanaRPCError(error)) {
 *     console.error(`RPC Error (status ${error.status}): ${error.rpcMessage}`);
 *     console.error(`Method: ${error.method}, Chain: ${error.chainId}`);
 *   }
 * }
 * ```
 */
export function isSolanaRPCError(error) {
    return error instanceof SolanaRPCError;
}
//# sourceMappingURL=data:application/json;base64,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